import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import PageHero from '../components/layout/PageHero';
import { contactInfo } from '../data/mock';

const KontaktPage = () => {
  const [searchParams] = useSearchParams();
  const service = searchParams.get('service');
  
  // Kontextabhängige Inhalte basierend auf Query-Parameter
  const isBeratung = service === 'beratung';
  
  const pageContent = useMemo(() => {
    if (isBeratung) {
      return {
        title: 'Persönliche Beratung vereinbaren',
        subtitle: 'Besprechen Sie Ihre Unternehmensbewertung mit unseren Experten – unverbindlich und kostenlos.',
        breadcrumb: 'Beratung vereinbaren',
        metaTitle: 'Persönliche Beratung zur Unternehmensbewertung vereinbaren',
        metaDescription: 'Vereinbaren Sie eine kostenlose, persönliche Beratung zur Unternehmensbewertung mit den Experten der Schweizer Nachfolge Experten AG.'
      };
    }
    // Standard-Kontaktseite
    return {
      title: 'Kontaktieren Sie uns',
      subtitle: 'Haben Sie Fragen zur Unternehmensbewertung oder möchten Sie eine persönliche Beratung? Wir sind für Sie da.',
      breadcrumb: 'Kontakt',
      metaTitle: 'Kontakt – Unternehmensbewertung & M&A Beratung anfragen',
      metaDescription: 'Kontaktieren Sie die Schweizer Nachfolge Experten AG. Beratung zu Unternehmensbewertung, Firmenverkauf und Nachfolge. Löwenstrasse 20, 8001 Zürich.'
    };
  }, [isBeratung]);

  const [formData, setFormData] = useState(() => ({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: service === 'beratung' ? 'beratung' : '',
    message: ''
  }));
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Senden fehlgeschlagen');
      setSubmitted(true);
    } catch (err) {
      setError('Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt per E-Mail.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Helmet>
        <title>{pageContent.metaTitle}</title>
        <meta name="description" content={pageContent.metaDescription} />
        <link rel="canonical" href="https://www.unternehmenbewerten.ch/kontakt" />
      </Helmet>
      
      <main className="pt-20">
        <PageHero
          title={pageContent.title}
          subtitle={pageContent.subtitle}
          breadcrumb={pageContent.breadcrumb}
        />

        {/* Contact Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Contact Form */}
              <div className="bg-[#f8f9fa] rounded-2xl p-8 border border-gray-100">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-[#FFE5E5] flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-8 h-8 text-[#FF0000]" />
                    </div>
                    <h3 className="heading-3 mb-4">Nachricht gesendet!</h3>
                    <p className="body-medium mb-6">
                      Vielen Dank für Ihre Anfrage. Wir melden uns in Kürze bei Ihnen.
                    </p>
                    <button 
                      onClick={() => setSubmitted(false)}
                      className="btn-secondary"
                    >
                      Neue Nachricht senden
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <h2 className="heading-3 mb-6">Nachricht senden</h2>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-[#5a5d5b] text-sm mb-2 block">Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#1a1c1b] placeholder-[#888b89] focus:outline-none focus:border-[#FF0000] transition-colors"
                          placeholder="Ihr Name"
                        />
                      </div>
                      <div>
                        <label className="text-[#5a5d5b] text-sm mb-2 block">E-Mail *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#1a1c1b] placeholder-[#888b89] focus:outline-none focus:border-[#FF0000] transition-colors"
                          placeholder="ihre@email.ch"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-[#5a5d5b] text-sm mb-2 block">Telefon</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#1a1c1b] placeholder-[#888b89] focus:outline-none focus:border-[#FF0000] transition-colors"
                          placeholder="+41 ..."
                        />
                      </div>
                      <div>
                        <label className="text-[#5a5d5b] text-sm mb-2 block">Unternehmen</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#1a1c1b] placeholder-[#888b89] focus:outline-none focus:border-[#FF0000] transition-colors"
                          placeholder="Firmenname"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[#5a5d5b] text-sm mb-2 block">Betreff *</label>
                      <select
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#1a1c1b] focus:outline-none focus:border-[#FF0000] transition-colors"
                      >
                        <option value="">Bitte auswählen...</option>
                        <option value="beratung">Persönliche Beratung zur Bewertung</option>
                        <option value="bewertung">Unternehmensbewertung</option>
                        <option value="verkauf">Firma verkaufen</option>
                        <option value="kauf">Firma kaufen</option>
                        <option value="ma-beratung">M&A Beratung</option>
                        <option value="sonstiges">Sonstiges</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[#5a5d5b] text-sm mb-2 block">Nachricht *</label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#1a1c1b] placeholder-[#888b89] focus:outline-none focus:border-[#FF0000] transition-colors resize-none"
                        placeholder="Wie können wir Ihnen helfen?"
                      />
                    </div>

                    <button type="submit" disabled={isSubmitting} className="btn-primary w-full gap-2" data-testid="contact-submit-btn">
                      {isSubmitting ? (
                        <span className="animate-pulse">Wird gesendet...</span>
                      ) : (
                        <>
                          <Send size={18} />
                          Nachricht senden
                        </>
                      )}
                    </button>

                    {error && (
                      <p className="text-red-600 text-sm text-center mt-2" data-testid="contact-error">{error}</p>
                    )}
                  </form>
                )}
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                <div>
                  <h2 className="heading-3 mb-6">Kontaktinformationen</h2>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#FFE5E5] flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#FF0000]" />
                      </div>
                      <div>
                        <p className="text-[#888b89] text-sm mb-1">Adresse</p>
                        <p className="text-[#1a1c1b] font-medium">Schweizer Nachfolge Experten AG</p>
                        <p className="text-[#1a1c1b]">c/o unternehmenbewerten.ch</p>
                        <p className="text-[#1a1c1b]">Löwenstrasse 20</p>
                        <p className="text-[#1a1c1b]">8001 Zürich</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#FFE5E5] flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-[#FF0000]" />
                      </div>
                      <div>
                        <p className="text-[#888b89] text-sm mb-1">Telefon</p>
                        <a 
                          href={`tel:${contactInfo.phone}`} 
                          className="text-[#1a1c1b] hover:text-[#FF0000] transition-colors"
                          onClick={() => {
                            if (typeof window !== 'undefined' && window.gtag) {
                              window.gtag('event', 'contact_phone_click', {
                                event_category: 'contact',
                                method: 'phone'
                              });
                            }
                          }}
                        >
                          {contactInfo.phone}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#FFE5E5] flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-[#FF0000]" />
                      </div>
                      <div>
                        <p className="text-[#888b89] text-sm mb-1">E-Mail</p>
                        <a 
                          href={`mailto:${contactInfo.email}`} 
                          className="text-[#1a1c1b] hover:text-[#FF0000] transition-colors"
                          onClick={() => {
                            if (typeof window !== 'undefined' && window.gtag) {
                              window.gtag('event', 'contact_email_click', {
                                event_category: 'contact',
                                method: 'email'
                              });
                            }
                          }}
                        >
                          {contactInfo.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#FFE5E5] flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-[#FF0000]" />
                      </div>
                      <div>
                        <p className="text-[#888b89] text-sm mb-1">Öffnungszeiten</p>
                        <p className="text-[#1a1c1b]">Montag – Freitag: 08:00 – 18:00</p>
                        <p className="text-[#888b89] text-sm">Termine nach Vereinbarung</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Google Maps */}
                <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2701.8876668252454!2d8.536373!3d47.3721!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47900a0a3d7d6b8f%3A0x5c6c5c8b5c5c5c5c!2sL%C3%B6wenstrasse%2020%2C%208001%20Z%C3%BCrich!5e0!3m2!1sde!2sch!4v1705268400000!5m2!1sde!2sch"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Standort Schweizer Nachfolge Experten AG"
                  ></iframe>
                </div>

                {/* Quick links */}
                <div className="bg-[#f8f9fa] rounded-2xl p-6 border border-gray-100">
                  <h3 className="text-[#1a1c1b] font-semibold mb-4">Schnellzugriff</h3>
                  <div className="space-y-3">
                    <Link to="/bewertung" className="flex items-center gap-2 text-[#5a5d5b] hover:text-[#FF0000] transition-colors">
                      <span>→</span>
                      <span>Unternehmenswert berechnen</span>
                    </Link>
                    <Link to="/unternehmensbewertung" className="flex items-center gap-2 text-[#5a5d5b] hover:text-[#FF0000] transition-colors">
                      <span>→</span>
                      <span>Professionelle Bewertung</span>
                    </Link>
                    <Link to="/ma-beratung" className="flex items-center gap-2 text-[#5a5d5b] hover:text-[#FF0000] transition-colors">
                      <span>→</span>
                      <span>M&A Beratung</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default KontaktPage;
